import { useGetPodData } from '@api';
import {
	FileUploadChip, Label, Spacer, Text, UploadControl, WarnChip, Loader, StatusChip, Tooltip
} from '@components';
import { CHIP_SIZE, CHIP_TYPE, ShipmentTarget, TOOLTIP_POSITION } from '@types';
import { formatDate } from '@utils';
import React, { ChangeEvent, MutableRefObject, useEffect, useState } from 'react';
import styles from './PODPanel.module.scss';

type UploadFile = {
	u_id: string;
	file: File;
	fileName: string;
	percentage: number;
	is_start: boolean;
	is_success: boolean;
	is_error: boolean;
	error_message?: string;
};

type ProgressInfo = {
	u_id: string;
	percentage: number;
	is_start: boolean;
	is_success: boolean;
	is_error: boolean;
};

type DocumentFile = {
	name: string;
	created_at: string;
	status: 'Pending verification' | 'Rejected' | 'Accepted';
};

type PODPanelProps = {
	shipmentTargets: ShipmentTarget[];
	singleUploader: MutableRefObject<any>;
	handleSelectFiles: (e: React.ChangeEvent<HTMLInputElement>, shipmentId: string) => void;
	filesToUpload: Record<string, UploadFile[]>;
	removeUploadFile: (fileId: string, shipmentId: string) => void;
	progressInfo: ProgressInfo[];
	isPODUnavailableShow?:boolean;
	handlePODUnavailable?: (e: boolean) => void;
	manifestId?: string;
	hidePreviousUploads?: boolean;
}

export const PODPanel = (props:PODPanelProps) => {
	const {
		shipmentTargets,
		singleUploader,
		handleSelectFiles,
		filesToUpload,
		removeUploadFile,
		progressInfo,
		isPODUnavailableShow,
		handlePODUnavailable,
		manifestId: manifestIdFromParent,
		hidePreviousUploads = false
	} = props;

	const manifestId = manifestIdFromParent ?? `${shipmentTargets[0]?.id}`
	const { callGetPodData, podData, loadingPodData } = useGetPodData();
	const [uploadedPODs, setUploadedPODs] = useState<DocumentFile[]>([]);

	const extractUploadedPODs = (data: any[]): DocumentFile[] => {
		return data.flatMap((entry) => {
			const podStatus: DocumentFile['status'] =
entry.is_confirmed === true
	? 'Accepted'
	: entry.last_rejected_at !== null
		? 'Rejected'
		: 'Pending verification';

			return (entry.pod_documents || []).map((doc: any) => ({
				name: doc.document?.name || '-',
				created_at: doc.document?.created_at || '-',
				status: podStatus,
			}));
		});
	};

	useEffect(() => {
		const fetchPodData = async () => {
			if (shipmentTargets.length === 1 && !hidePreviousUploads) {
				setUploadedPODs([])
				await callGetPodData({ routes: [manifestId] });
			}
		};

		fetchPodData();
	}, [shipmentTargets]);

	useEffect(() => {
		if (podData) {
			const extracted = extractUploadedPODs(podData);
			setUploadedPODs(extracted);
		}
	}, [podData]);

	const isError = progressInfo && progressInfo?.find((o: { is_error: boolean; }) => o.is_error === true)

	return (

		<div className={styles.container}>
			<Text text={`Please ensure you have the correct files uploaded as you won't be able to edit later.`} />
			<Spacer size={10} />
			{shipmentTargets.length <= 1 && <>
				<UploadControl
					isPODUnavailableShow={isPODUnavailableShow}
					ref={singleUploader}
					handleFiles={(e: ChangeEvent<HTMLInputElement>) => { handleSelectFiles(e, manifestId) }}
					fileTypeText={'proof of deliveries (POD)'}
					progressInfo={[]}
					handlePODUnavailable={(e: boolean) => handlePODUnavailable?.(e)}
				/>
				<Spacer size={10} />
				{Object.prototype.hasOwnProperty.call(filesToUpload, manifestId) && filesToUpload[manifestId].map((upload:any, i:number) => {
					return <React.Fragment key={upload.u_id}>
						{i === 0 && <Label text='PODs being uploaded:' />}
						<FileUploadChip name={upload.fileName} id={upload.u_id} removeFile={(id:string) => { removeUploadFile(id, manifestId) }} progressInfo={progressInfo} />
					</React.Fragment>
				})}
				<Spacer size={10} />

				{
					progressInfo &&
					isError &&
					<WarnChip text={`There was an error uploading one or more files. Please try again.`} showIcon />
				}
				<Spacer size={5} />

				{loadingPodData ? (
					<div className={styles.loader}>
						<Loader />
					</div>
				) : uploadedPODs?.length > 0 ? (
					<>
						<Label text={`Uploaded POD's:`} />
						<Spacer size={5} />
						{uploadedPODs.map((file, idx) => (
							<div key={idx} className={styles.history}>
								<div className={styles.fileName}>
									<Text text={file.name} />
								</div>
								<div className={styles.fileInfo}>
									<StatusChip
										text={file.status}
										type={
											file.status === 'Accepted'
												? CHIP_TYPE.SUCCESS
												: file.status === 'Rejected'
													? CHIP_TYPE.ERROR
													: CHIP_TYPE.WARN
										}
										size={CHIP_SIZE.MEDIUM}
									/>
									<Tooltip text='Date of upload' position={TOOLTIP_POSITION.TOP}>
										<Text text={formatDate(new Date(file.created_at))} />
									</Tooltip>
								</div>
							</div>
						))}
					</>
				) : null}
			</>}

		</div>
	);
}
